import * as React from 'react';
import type { TFunction } from 'i18next';
import { buildMediaTypeOptionLabels } from '../../../lib/media-type-options';
import { buildRangePillLabels } from '../../../lib/build-range-pill-labels';
import type { FilterFieldLabels } from '../lib/filter-fields';

type FilterPanelLabels = {
  allLabel: string;
  searchPlaceholder: string;
  getSelectedCountLabel: (count: number) => string;
  getClearAriaLabel: (fieldId: string) => string;
  clearAllLabel: string;
  addFilterLabel: string;
  rangeLabels: { minLabel: string; maxLabel: string; applyLabel: string; clearLabel: string };
};

/**
 * Column-parity filter labels (catalog-column-filter-parity) — extracted so
 * `buildFilterFieldLabels` stays within the file's max-lines-per-function
 * budget. `status`/`supplierEmail`/`supplierPhone`/`supplierContactInfo` are
 * deliberately absent (CU-86cb1nq7h, client request/correction — removed
 * entirely) — see `FilterFieldLabels` doc comment.
 */
function buildColumnParityLabels(t: TFunction<'inventory'>) {
  return {
    notes: t('filters.notes'),
    roadNumber: t('filters.roadNumber'),
  };
}

/** Builds the `FilterFieldLabels` object — extracted so `useFilterLabels` stays within the file's max-lines-per-function budget. */
function buildFilterFieldLabels(
  t: TFunction<'inventory'>,
  tMap: TFunction<'map'>,
): FilterFieldLabels {
  return {
    region: t('filters.region'),
    county: t('filters.county'),
    agglomeration: t('filters.agglomeration'),
    city: t('filters.city'),
    formatCode: t('filters.formatCode'),
    mediaType: t('filters.mediaType'),
    supplierId: t('filters.supplierId'),
    lighting: t('filters.lighting'),
    supplierNumber: t('filters.supplierNumber'),
    grwId: t('filters.grwId'),
    searchPlaceholder: t('filters.searchPlaceholder'),
    mediaTypeOptionLabels: buildMediaTypeOptionLabels(tMap),
    // Combined "Lokalizacja" field — see buildLocationFilterField.
    location: t('filters.location'),
    locationSearchPlaceholder: t('filters.locationSearchPlaceholder'),
    locationInfoHint: t('filters.locationInfoHint'),
    locationInfoHintLabel: t('filters.locationInfoHintLabel'),
    administrativeArea: t('filters.administrativeArea'),
    // ─── New catalog filter labels (CU-86cataqt9) ───
    material: t('filters.material'),
    multiformat: t('filters.multiformat'),
    tags: t('filters.tags'),
    address: t('filters.address'),
    area: t('filters.area'),
    width: t('filters.width'),
    height: t('filters.height'),
    audience: t('filters.audience'),
    catalogPrice: t('filters.catalogPrice'),
    rent: t('filters.rent'),
    // ─── Remaining displayed cost-column range filters ───
    exposureCost: t('filters.exposureCost'),
    additionalCosts: t('filters.additionalCosts'),
    discountedPrice: t('filters.discountedPrice'),
    lastMinutePrice: t('filters.lastMinutePrice'),
    starterPrice: t('filters.starterPrice'),
    productionCost: t('filters.productionCost'),
    installationCost: t('filters.installationCost'),
    dismantlingCost: t('filters.dismantlingCost'),
    clientProduction: t('filters.clientProduction'),
    clientInstallation: t('filters.clientInstallation'),
    clientDismantling: t('filters.clientDismantling'),
    clientAdditionalFees: t('filters.clientAdditionalFees'),
    ...buildColumnParityLabels(t),
  };
}

/** Builds the `FilterPanelLabels` object — extracted for the same reason as `buildFilterFieldLabels`. */
function buildFilterPanelLabels(t: TFunction<'inventory'>): FilterPanelLabels {
  return {
    allLabel: t('filters.allLabel'),
    searchPlaceholder: t('filters.searchPlaceholder'),
    getSelectedCountLabel: (count: number) => t('filters.selectedCountLabel', { n: count }),
    getClearAriaLabel: (fieldId: string) => `${t('filters.clearLabel')} ${fieldId}`,
    clearAllLabel: t('filters.clearAllLabel'),
    addFilterLabel: t('filters.addFilterLabel'),
    rangeLabels: buildRangePillLabels(t),
  };
}

/**
 * Builds memoised label objects for filter fields and the filter panel.
 * Both objects are re-derived only when the translation function changes (i.e. on locale switch).
 *
 * `tMap` supplies the `map`-namespaced media-type labels (`legend.types.*`) reused for the
 * media-type filter options.
 */
export function useFilterLabels(
  t: TFunction<'inventory'>,
  tMap: TFunction<'map'>,
): {
  filterFieldLabels: FilterFieldLabels;
  filterPanelLabels: FilterPanelLabels;
} {
  const filterFieldLabels = React.useMemo(() => buildFilterFieldLabels(t, tMap), [t, tMap]);
  const filterPanelLabels = React.useMemo(() => buildFilterPanelLabels(t), [t]);

  return { filterFieldLabels, filterPanelLabels };
}
